/**
 * quickBillForm — the arithmetic and rules behind the invoice-style Quick Bill
 * form. Pure: the screen feeds it rows, it answers with amounts and problems.
 *
 * ── One engine ──────────────────────────────────────────────────────────────
 * The form does no tax maths of its own. Each row becomes the same
 * ProductForBilling the cart uses and goes through computeLineItem /
 * computeInvoiceTotals, so what the form shows is exactly what the bill will
 * say once the rows are loaded into the cart and written.
 *
 * ── Rate ────────────────────────────────────────────────────────────────────
 * On a tax invoice the Rate column is before GST, so that is the default. A
 * shopkeeper who thinks in tax-inclusive prices ticks "Rates include GST".
 * Either way the typed rate is converted to the STORE's stored convention
 * (TAX_INCLUSIVE setting) before the engine sees it — the engine reads
 * `sellingPrice` in that convention, as it does for every catalogue product.
 */
import type { BillingConfig, ComputedLineItem, InvoiceTotals, ProductForBilling } from './billingEngine';
import { computeInvoiceTotals, computeLineItem, toRupees } from './billingEngine';
import { buildManualBillingProduct } from './manualItem';
import { toStoredSellingPrice } from './priceMode';

export interface QuickBillRow {
  /** Stable React key; unrelated to any database id. */
  key: string;
  /** The catalogue product picked for this line, or null for a typed item. */
  catalogue: ProductForBilling | null;
  name: string;
  hsn: string;
  qty: number;
  unit: string;
  /** As typed — before GST unless `ratesIncludeGst`. */
  rate: number;
  gstRate: number;
  cessRate: number;
}

let seq = 0;
export function emptyRow(): QuickBillRow {
  seq += 1;
  return { key: `qb-${Date.now().toString(36)}-${seq}`, catalogue: null, name: '', hsn: '', qty: 1, unit: 'NOS', rate: 0, gstRate: 18, cessRate: 0 };
}

/** A row nobody has started — the trailing empty line the form always keeps. */
export function isBlankRow(r: QuickBillRow): boolean {
  return !r.catalogue && r.name.trim() === '' && r.rate === 0;
}

export interface RateMode {
  ratesIncludeGst: boolean;
  config: BillingConfig;
}

/** The engine's view of one row, priced in the store's stored convention. */
export function rowProduct(r: QuickBillRow, mode: RateMode): ProductForBilling {
  const stored = toStoredSellingPrice(r.rate, r.gstRate, r.cessRate, mode.ratesIncludeGst, mode.config.isTaxInclusive);
  const hsn = r.hsn.trim() || null;
  if (r.catalogue) {
    // Keep the product's identity (stock tracking, id) but bill what is on
    // the form: the line may override name, HSN, slab, unit and rate.
    return {
      ...r.catalogue,
      name: r.name.trim() || r.catalogue.name,
      sellingPrice: stored,
      hsnCode: hsn,
      gstRate: r.gstRate,
      cessRate: r.cessRate,
      resolvedGstRate: undefined,
      resolvedCessRate: undefined,
      unit: r.unit || r.catalogue.unit,
    };
  }
  return buildManualBillingProduct({ name: r.name, hsn, rate: stored, gstRate: r.gstRate, cessRate: r.cessRate, unit: r.unit || 'NOS' });
}

export interface FormLine { key: string; taxable: number; total: number }
export interface FormResult {
  lines: FormLine[];
  totals: InvoiceTotals;
  adjustedItems: ComputedLineItem[];
}

/** Amounts for every non-blank row, and the invoice totals, via the engine. */
export function computeForm(rows: readonly QuickBillRow[], isInterState: boolean, mode: RateMode): FormResult {
  const live = rows.filter((r) => !isBlankRow(r));
  const computed = live.map((r) => {
    const p = rowProduct(r, mode);
    return computeLineItem(p, r.qty, p.sellingPrice, 0, isInterState, mode.config);
  });
  const { totals, adjustedItems } = computeInvoiceTotals(computed, 0, isInterState, mode.config);
  return {
    lines: live.map((r, i) => ({
      key: r.key,
      taxable: toRupees(adjustedItems[i].taxableAmountPaisa),
      total: toRupees(adjustedItems[i].totalPaisa),
    })),
    totals,
    adjustedItems,
  };
}

/**
 * Everything that stops the bill, in words, top to bottom. Empty = ready.
 * HSN is not required here: it is mandatory on B2B invoices above a turnover
 * threshold, which this screen cannot know — the missing-HSN hint is shown on
 * the line instead.
 */
export function validateForm(rows: readonly QuickBillRow[], buyerName: string): string[] {
  const problems: string[] = [];
  const live = rows.filter((r) => !isBlankRow(r));
  if (!buyerName.trim()) problems.push('Enter the buyer’s name.');
  if (live.length === 0) problems.push('Add at least one item.');
  live.forEach((r, i) => {
    const n = i + 1;
    if (!r.name.trim()) problems.push(`Line ${n}: enter a description.`);
    if (!(r.qty > 0)) problems.push(`Line ${n}: quantity must be more than 0.`);
    if (!(r.rate >= 0) || !Number.isFinite(r.rate)) problems.push(`Line ${n}: enter a valid rate.`);
  });
  // The cart keys lines by product, so the same catalogue product on two lines
  // would merge into one at the first line's rate. Ask for one line instead.
  const seen = new Map<string, number>();
  live.forEach((r, i) => {
    if (!r.catalogue) return;
    const first = seen.get(r.catalogue.id);
    if (first !== undefined) problems.push(`Lines ${first} and ${i + 1} are the same product — combine them into one line.`);
    else seen.set(r.catalogue.id, i + 1);
  });
  return problems;
}

export interface CatalogueCandidate {
  name: string;
  hsn: string | null;
  gstRate: number;
  cessRate: number;
  unit: string;
  /** In the store's stored convention, ready for products.selling_price. */
  sellingPrice: number;
}

/**
 * Typed items to add to the catalogue after the bill is saved: rows that are
 * not catalogue picks, one per name (case-insensitive), skipping names the
 * catalogue already has.
 */
export function catalogueCandidates(
  rows: readonly QuickBillRow[],
  mode: RateMode,
  existingNames: readonly string[],
): CatalogueCandidate[] {
  const taken = new Set(existingNames.map((n) => n.trim().toLowerCase()));
  const out: CatalogueCandidate[] = [];
  for (const r of rows) {
    if (r.catalogue || isBlankRow(r)) continue;
    const name = r.name.trim();
    const k = name.toLowerCase();
    if (!name || taken.has(k)) continue;
    taken.add(k);
    out.push({
      name,
      hsn: r.hsn.trim() || null,
      gstRate: r.gstRate,
      cessRate: r.cessRate,
      unit: r.unit || 'NOS',
      sellingPrice: toStoredSellingPrice(r.rate, r.gstRate, r.cessRate, mode.ratesIncludeGst, mode.config.isTaxInclusive),
    });
  }
  return out;
}
